// src/services/wechatService.ts
import {
  getWechatJsConfig,
  wechatLogin,
  bindWechatOpenid,
  getWechatBinding,
  updateWechatBinding,
  isWechatEnvironment,
  isWechatDevtools,
  getSignatureUrl,
  buildWechatAuthUrl,
  type WechatBindResponse,
  type WechatUserBinding,
} from "@/api/wechat";
import { reportError, reportMessage } from "@/utils/sentry";
import { notificationService } from "@/services/notificationService";
import { jwtService } from "@/services/jwt";
import { configService } from "@/services/configService";
import { errorHandlingService } from "@/services/errorHandlingService";

// 微信 JS-SDK 状态管理
export type WechatSDKStatus =
  | "uninitialized"
  | "initializing"
  | "ready"
  | "error";

export interface WechatSDKConfig {
  debug?: boolean;
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
  openTagList?: string[];
}

export interface WechatEnvironmentInfo {
  isWechat: boolean;
  isDevtools: boolean;
  isWKWebView: boolean;
  userAgent: string;
  version?: string;
}

export interface WechatAuthOptions {
  scope?: "snsapi_base" | "snsapi_userinfo";
  state?: string;
  forcePopup?: boolean;
}

export interface WechatLoginResult {
  openid: string;
  sessionKey?: string;
  isNewBinding?: boolean;
  userInfo?: WechatUserBinding;
}

export class WechatService {
  private sdkStatus: WechatSDKStatus = "uninitialized";
  private isEnabled: boolean = false;
  private currentConfig: WechatSDKConfig | null = null;
  private initPromise: Promise<boolean> | null = null;
  private retryCount: number = 0;
  private readonly maxRetries: number = 3;
  private readonly retryDelay: number = 1000;

  constructor() {
    this.isEnabled = this.checkWechatFeatureEnabled();

    // 只在微信环境中进行初始化
    if (this.isEnabled && this.isInWechatEnvironment()) {
      this.setupWechatEnvironment();
    }
  }

  /** 检查微信功能是否启用 */
  private checkWechatFeatureEnabled(): boolean {
    const enabled = configService.isWechatEnabled();
    if (!enabled) {
      console.log("[WechatService] 微信功能已禁用");
    }
    return enabled;
  }

  /** 获取微信环境信息 */
  public getEnvironmentInfo(): WechatEnvironmentInfo {
    const userAgent = navigator.userAgent;
    const isWechat = isWechatEnvironment();
    const isDevtools = isWechatDevtools();

    // 检测是否为 WKWebView
    const isWKWebView = this.isWKWebView();

    // 尝试解析微信版本
    const versionMatch = userAgent.match(/MicroMessenger\/(\d+\.\d+\.\d+)/);
    const version = versionMatch ? versionMatch[1] : undefined;

    return {
      isWechat,
      isDevtools,
      isWKWebView,
      userAgent,
      version,
    };
  }

  /** 检测是否为 WKWebView 环境 */
  private isWKWebView(): boolean {
    if (typeof window === "undefined") return false;

    // 通过微信注入的变量检测
    return !!(window as any).__wxjs_is_wkwebview;
  }

  /** 检查是否在微信环境中 */
  public isInWechatEnvironment(): boolean {
    return isWechatEnvironment() || isWechatDevtools();
  }

  /** 检查微信功能是否可用 */
  public isAvailable(): boolean {
    return this.isEnabled && this.isInWechatEnvironment();
  }

  /** 获取当前 SDK 状态 */
  public getSDKStatus(): WechatSDKStatus {
    return this.sdkStatus;
  }

  /** 设置微信环境 */
  private setupWechatEnvironment(): void {
    const envInfo = this.getEnvironmentInfo();

    reportMessage("微信环境信息", "info", {
      action: "setup_wechat_environment",
      envInfo,
      timestamp: new Date().toISOString(),
    });

    // 监听微信 JS-SDK 错误事件
    this.setupErrorHandlers();

    // 监听开放标签错误事件
    this.setupOpenTagErrorHandlers();

    // 处理 WKWebView 兼容性
    if (envInfo.isWKWebView) {
      this.handleWKWebViewCompatibility();
    }
  }

  /** 设置错误处理器 */
  private setupErrorHandlers(): void {
    // 微信 JS-SDK ready 事件
    document.addEventListener(
      "WeixinJSBridgeReady",
      () => {
        reportMessage("WeixinJSBridge Ready", "info");
      },
      false,
    );

    // 监听微信客户端异常
    window.addEventListener("error", (event) => {
      if (
        event.message?.includes("wx") ||
        event.filename?.includes("jweixin")
      ) {
        reportError(event.error || new Error(event.message), {
          type: "wechat_js_error",
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      }
    });
  }

  /** 设置开放标签错误处理器 */
  private setupOpenTagErrorHandlers(): void {
    document.addEventListener("WeixinOpenTagsError", (event: any) => {
      const errorMsg = event.detail?.errMsg || "未知开放标签错误";
      reportError(new Error(errorMsg), {
        type: "wechat_opentag_error",
        detail: event.detail,
      });

      // 显示用户友好的错误提示
      notificationService.warning("wechat.versionNotSupported");
    });
  }

  /** 处理 WKWebView 兼容性问题 */
  private handleWKWebViewCompatibility(): void {
    // WKWebView 下的特殊处理
    reportMessage("检测到 WKWebView 环境，启用兼容性处理", "info");

    // 监听页面显示/隐藏事件（WKWebView 特有）
    document.addEventListener("visibilitychange", () => {
      if (
        document.visibilityState === "visible" &&
        this.sdkStatus === "ready"
      ) {
        // 页面重新可见时，可能需要重新初始化某些功能
        this.handlePageVisibilityChange();
      }
    });

    // 监听页面 pageshow 事件
    window.addEventListener("pageshow", (event) => {
      if (event.persisted && this.sdkStatus === "ready") {
        // 从缓存恢复的页面，可能需要重新初始化
        this.handlePageRestore();
      }
    });
  }

  /** 处理页面可见性变化 */
  private handlePageVisibilityChange(): void {
    reportMessage("页面可见性变化，检查微信 SDK 状态", "info");
    // 在 WKWebView 中，页面重新显示时可能需要重新配置
    if (this.currentConfig) {
      this.validateSDKStatus();
    }
  }

  /** 处理页面恢复 */
  private handlePageRestore(): void {
    reportMessage("页面从缓存恢复，重新验证微信 SDK", "info");
    // 从浏览器缓存恢复的页面可能需要重新初始化 SDK
    if (this.currentConfig) {
      this.reinitializeSDK();
    }
  }

  /** 验证 SDK 状态 */
  private validateSDKStatus(): void {
    if (typeof window.wx !== "undefined" && window.wx.checkJsApi) {
      window.wx.checkJsApi({
        jsApiList: ["chooseImage"], // 使用一个基础 API 来检测
        success: (res: any) => {
          if (!res.checkResult.chooseImage) {
            reportMessage("微信 SDK 状态异常，尝试重新初始化", "warning");
            this.reinitializeSDK();
          }
        },
        fail: () => {
          reportMessage("微信 SDK 检查失败，尝试重新初始化", "warning");
          this.reinitializeSDK();
        },
      });
    }
  }

  /** 重新初始化 SDK */
  private async reinitializeSDK(): Promise<void> {
    if (this.currentConfig) {
      this.sdkStatus = "uninitialized";
      this.initPromise = null;
      await this.initializeSDK();
    }
  }

  /** 初始化微信 JS-SDK */
  public async initializeSDK(
    customConfig?: Partial<WechatSDKConfig>,
  ): Promise<boolean> {
    if (!this.isAvailable()) {
      throw new Error("微信功能未启用或不在微信环境中");
    }

    if (this.initPromise) {
      return await this.initPromise;
    }

    this.initPromise = this.performSDKInitialization(customConfig);
    return await this.initPromise;
  }

  /** 执行 SDK 初始化 */
  private async performSDKInitialization(
    customConfig?: Partial<WechatSDKConfig>,
  ): Promise<boolean> {
    try {
      this.sdkStatus = "initializing";

      // 确保微信 JS-SDK 脚本已加载
      await this.ensureWechatScriptLoaded();

      // 获取当前页面 URL 用于签名
      const currentUrl = getSignatureUrl();

      // 获取微信 JS-SDK 配置
      const config = await getWechatJsConfig(currentUrl);

      // 合并自定义配置
      const wechatConfig = configService.getWechatConfig();
      const finalConfig: WechatSDKConfig = {
        debug: wechatConfig.debug, // 使用配置服务中的调试设置
        ...config,
        jsApiList: config.jsApiList || this.getDefaultJsApiList(),
        openTagList: customConfig?.openTagList || [],
        ...customConfig,
      };

      this.currentConfig = finalConfig;

      // 配置微信 JS-SDK
      await this.configureWechatSDK(finalConfig);

      this.sdkStatus = "ready";
      this.retryCount = 0;

      reportMessage("微信 JS-SDK 初始化成功", "info", {
        action: "initialize_wechat_sdk",
        appId: finalConfig.appId,
        jsApiList: finalConfig.jsApiList,
        timestamp: new Date().toISOString(),
      });

      return true;
    } catch (error) {
      this.sdkStatus = "error";
      this.initPromise = null;

      // 使用统一的错误处理服务
      await errorHandlingService.handleError(error, "wechat_sdk_error", {
        action: "sdk_initialization",
        retryCount: this.retryCount,
        url: getSignatureUrl(),
        config: customConfig,
      });

      // 重试机制
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;

        // 延迟重试
        await new Promise((resolve) =>
          setTimeout(resolve, this.retryDelay * this.retryCount),
        );
        return await this.performSDKInitialization(customConfig);
      }

      this.retryCount = 0; // 重置重试计数
      throw error;
    }
  }

  /** 确保微信 JS-SDK 脚本已加载 */
  private async ensureWechatScriptLoaded(): Promise<void> {
    if (typeof window.wx !== "undefined") {
      return; // 已加载
    }

    // 动态加载微信 JS-SDK 脚本
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
      script.async = true;

      script.onload = () => {
        if (typeof window.wx !== "undefined") {
          resolve();
        } else {
          reject(new Error("微信 JS-SDK 脚本加载失败"));
        }
      };

      script.onerror = () => {
        // 尝试备用 CDN
        const backupScript = document.createElement("script");
        backupScript.src = "https://res2.wx.qq.com/open/js/jweixin-1.6.0.js";
        backupScript.async = true;

        backupScript.onload = () => {
          if (typeof window.wx !== "undefined") {
            resolve();
          } else {
            reject(new Error("微信 JS-SDK 备用脚本也加载失败"));
          }
        };

        backupScript.onerror = () => {
          reject(new Error("微信 JS-SDK 脚本加载失败"));
        };

        document.head.appendChild(backupScript);
      };

      document.head.appendChild(script);
    });
  }

  /** 配置微信 JS-SDK */
  private async configureWechatSDK(config: WechatSDKConfig): Promise<void> {
    return new Promise((resolve, reject) => {
      window.wx.config({
        debug: config.debug,
        appId: config.appId,
        timestamp: config.timestamp,
        nonceStr: config.nonceStr,
        signature: config.signature,
        jsApiList: config.jsApiList,
        openTagList: config.openTagList,
      });

      window.wx.ready(() => {
        reportMessage("微信 JS-SDK 配置成功", "info");
        resolve();
      });

      window.wx.error((res: any) => {
        const error = new Error(`微信 JS-SDK 配置失败: ${res.errMsg}`);
        reportError(error, {
          type: "wechat_sdk_config_error",
          detail: res,
          config: {
            appId: config.appId,
            timestamp: config.timestamp,
            jsApiList: config.jsApiList,
          },
        });
        reject(error);
      });
    });
  }

  /** 获取默认的 JS API 列表 */
  private getDefaultJsApiList(): string[] {
    return [
      // 基础接口
      "checkJsApi",

      // 分享接口
      "updateAppMessageShareData",
      "updateTimelineShareData",

      // 图像接口
      "chooseImage",
      "previewImage",
      "uploadImage",
      "downloadImage",

      // 设备信息
      "getNetworkType",

      // 地理位置
      "getLocation",
      "openLocation",

      // 界面操作
      "closeWindow",
      "hideMenuItems",
      "showMenuItems",

      // 微信扫一扫
      "scanQRCode",
    ];
  }

  /** 构建微信授权登录 URL */
  public buildAuthUrl(options: WechatAuthOptions = {}): string {
    if (!this.isAvailable()) {
      throw new Error("微信功能未启用或不在微信环境中");
    }

    const wechatConfig = configService.getWechatConfig();
    const appId = this.currentConfig?.appId || wechatConfig.appId;
    if (!appId) {
      throw new Error("未找到微信 AppID 配置");
    }

    const redirectUri = encodeURIComponent(window.location.href.split("#")[0]);
    const scope = options.scope || "snsapi_base";
    const state = options.state || this.generateRandomState();

    return buildWechatAuthUrl(appId, redirectUri, scope, state);
  }

  /** 生成随机状态字符串 */
  private generateRandomState(): string {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  /** 处理微信授权回调 */
  public async handleAuthCallback(
    code: string,
    state?: string,
  ): Promise<WechatLoginResult> {
    if (!this.isAvailable()) {
      throw new Error("微信功能未启用或不在微信环境中");
    }

    try {
      // 通过 code 获取 openid
      const loginResult = await wechatLogin(code);

      if (!loginResult.openid) {
        throw new Error("微信登录失败：未获取到 openid");
      }

      // 检查是否已绑定用户
      let userBinding: WechatUserBinding | null = null;
      let isNewBinding = true;

      try {
        userBinding = await getWechatBinding(loginResult.openid);
        isNewBinding = false;
      } catch (error: any) {
        // 如果是 404 错误，说明还未绑定
        if (error.response?.status !== 404) {
          throw error;
        }
      }

      const result: WechatLoginResult = {
        openid: loginResult.openid,
        sessionKey: loginResult.session_key,
        isNewBinding,
        userInfo: userBinding || undefined,
      };

      reportMessage("微信授权回调处理成功", "info", {
        action: "handle_auth_callback",
        openid: loginResult.openid.substring(0, 8) + "***",
        isNewBinding,
        hasUserInfo: !!userBinding,
        timestamp: new Date().toISOString(),
      });

      return result;
    } catch (error) {
      reportError(error as Error, {
        type: "wechat_auth_callback_error",
        code: code ? "provided" : "empty",
        state,
      });
      throw error;
    }
  }

  /** 绑定微信账号到用户 */
  public async bindAccount(
    openid: string,
    userid: string,
  ): Promise<WechatBindResponse> {
    if (!this.isAvailable()) {
      throw new Error("微信功能未启用或不在微信环境中");
    }

    const result = await bindWechatOpenid(openid, userid);

    // 绑定成功后自动设置 JWT tokens
    if (result.access && result.refresh) {
      await jwtService.setTokens(result.access, result.refresh);
    }

    return result;
  }

  /** 更新微信账号绑定 */
  public async updateBinding(
    openid: string,
    userid: string,
  ): Promise<WechatBindResponse> {
    if (!this.isAvailable()) {
      throw new Error("微信功能未启用或不在微信环境中");
    }

    const result = await updateWechatBinding(openid, userid);

    // 更新成功后自动设置新的 JWT tokens
    if (result.access && result.refresh) {
      await jwtService.setTokens(result.access, result.refresh);
    }

    return result;
  }

  /** 获取微信绑定信息 */
  public async getBindingInfo(openid: string): Promise<WechatUserBinding> {
    if (!this.isAvailable()) {
      throw new Error("微信功能未启用或不在微信环境中");
    }

    return await getWechatBinding(openid);
  }

  /** 等待 SDK 准备就绪 */
  public async waitForSDKReady(): Promise<boolean> {
    if (this.sdkStatus === "ready") {
      return true;
    }

    if (this.sdkStatus === "uninitialized") {
      return await this.initializeSDK();
    }

    if (this.sdkStatus === "initializing" && this.initPromise) {
      return await this.initPromise;
    }

    if (this.sdkStatus === "error") {
      throw new Error("微信 SDK 处于错误状态，请重新初始化");
    }

    return false;
  }

  /** 重置服务状态 */
  public reset(): void {
    this.sdkStatus = "uninitialized";
    this.currentConfig = null;
    this.initPromise = null;
    this.retryCount = 0;

    reportMessage("微信服务已重置", "info");
  }

  /** 保存 openid 到 localStorage */
  public saveOpenidToStorage(openid: string): void {
    try {
      localStorage.setItem("wechat_openid", openid);
      localStorage.setItem("wechat_openid_timestamp", Date.now().toString());

      reportMessage("微信 openid 保存到 localStorage", "info", {
        action: "save_openid_to_storage",
        openid: openid.substring(0, 8) + "***",
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      reportError(error as Error, {
        type: "wechat_storage_error",
        action: "save_openid",
        openid: openid ? openid.substring(0, 8) + "***" : "empty",
      });
      throw error;
    }
  }

  /** 从 localStorage 读取 openid */
  public getOpenidFromStorage(): string | null {
    try {
      const openid = localStorage.getItem("wechat_openid");
      const timestamp = localStorage.getItem("wechat_openid_timestamp");

      // 检查是否过期（24小时）
      if (openid && timestamp) {
        const savedTime = parseInt(timestamp);
        const now = Date.now();
        const expireTime = 24 * 60 * 60 * 1000; // 24小时

        if (now - savedTime > expireTime) {
          // 已过期，清除存储
          this.clearOpenidFromStorage();
          reportMessage("微信 openid 已过期，已清除", "info");
          return null;
        }

        reportMessage("从 localStorage 读取微信 openid", "info", {
          action: "get_openid_from_storage",
          openid: openid.substring(0, 8) + "***",
          age: Math.floor((now - savedTime) / 1000 / 60), // 分钟
        });

        return openid;
      }

      return null;
    } catch (error) {
      reportError(error as Error, {
        type: "wechat_storage_error",
        action: "get_openid",
      });
      return null;
    }
  }

  /** 清除 localStorage 中的 openid */
  public clearOpenidFromStorage(): void {
    try {
      localStorage.removeItem("wechat_openid");
      localStorage.removeItem("wechat_openid_timestamp");

      reportMessage("清除 localStorage 中的微信 openid", "info", {
        action: "clear_openid_from_storage",
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      reportError(error as Error, {
        type: "wechat_storage_error",
        action: "clear_openid",
      });
    }
  }

  /** 获取或重新获取 openid */
  public async getOrFetchOpenid(): Promise<string | null> {
    // 首先尝试从 localStorage 获取
    const cachedOpenid = this.getOpenidFromStorage();
    if (cachedOpenid) {
      return cachedOpenid;
    }

    // 如果没有缓存的 openid，检查是否在微信环境中
    if (!this.isInWechatEnvironment()) {
      reportMessage("不在微信环境中，无法获取 openid", "warning");
      return null;
    }

    // 检查 URL 参数中是否有微信授权回调的 code
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");

    if (code) {
      try {
        // 使用 code 获取 openid
        reportMessage("检测到微信授权码，开始获取 openid", "info", {
          code: code.substring(0, 10) + "...",
          state,
        });

        const loginResult = await wechatLogin(code);
        if (loginResult.openid) {
          // 保存到 localStorage
          this.saveOpenidToStorage(loginResult.openid);
          return loginResult.openid;
        }
      } catch (error) {
        reportError(error as Error, {
          type: "wechat_fetch_openid_error",
          code: code ? code.substring(0, 10) + "..." : "empty",
          state,
        });
      }
    }

    // 如果没有 code 或获取失败，需要重新发起微信授权
    reportMessage("未找到有效的微信 openid，需要重新授权", "info");
    return null;
  }

  /** 重新发起微信授权以获取 openid */
  public redirectToWechatAuth(): void {
    if (!this.isInWechatEnvironment()) {
      throw new Error("不在微信环境中，无法发起微信授权");
    }

    try {
      const authUrl = this.buildAuthUrl({
        scope: "snsapi_userinfo", // 使用 userinfo 获取用户信息
        state: `auth_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      });

      reportMessage("重定向到微信授权页面", "info", {
        action: "redirect_to_wechat_auth",
        authUrl: authUrl.substring(0, 100) + "...",
        timestamp: new Date().toISOString(),
      });

      window.location.href = authUrl;
    } catch (error) {
      reportError(error as Error, {
        type: "wechat_auth_redirect_error",
        action: "redirect_to_wechat_auth",
      });
      throw error;
    }
  }

  /** 获取用户当前绑定的微信 openid */
  public async getCurrentUserWechatOpenid(): Promise<string | null> {
    try {
      // 首先尝试从存储中获取
      let openid = this.getOpenidFromStorage();

      if (!openid) {
        // 如果存储中没有，尝试重新获取
        openid = await this.getOrFetchOpenid();
      }

      if (!openid) {
        // 如果仍然没有，可能需要用户重新授权
        reportMessage("无法获取当前用户的微信 openid", "warning", {
          action: "get_current_user_wechat_openid",
          reason: "未找到有效的 openid",
        });
        return null;
      }

      // 验证 openid 是否有效（直接调用 API，不依赖微信环境检查）
      try {
        await getWechatBinding(openid);
        return openid;
      } catch (error: any) {
        if (error.response?.status === 404) {
          // openid 存在但未绑定账号，这是正常情况
          return openid;
        }

        // 只有在确认 openid 无效时才清除缓存（如401未授权等）
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.clearOpenidFromStorage();
          reportMessage("微信 openid 无效，已清除缓存", "warning", {
            openid: openid.substring(0, 8) + "***",
            error: error.message,
            status: error.response?.status,
          });
          return null;
        }

        // 其他错误（如网络错误、服务器错误）不应该清除有效的 openid
        reportMessage("微信 openid 验证遇到临时错误，保留 openid", "info", {
          openid: openid.substring(0, 8) + "***",
          error: error.message,
          status: error.response?.status,
        });
        return openid;
      }
    } catch (error) {
      reportError(error as Error, {
        type: "wechat_get_current_openid_error",
        action: "get_current_user_wechat_openid",
      });
      return null;
    }
  }

  /** 销毁服务 */
  public destroy(): void {
    this.reset();
    // 清理事件监听器等资源
    reportMessage("微信服务已销毁", "info");
  }
}

// 全局微信服务实例

export const wechatService = new WechatService();

// 类型声明扩展
declare global {
  interface Window {
    wx: any;
    __wxjs_is_wkwebview?: boolean;
  }
}
